import type { Column } from "@tanstack/react-table";
import { Check, PlusCircle } from "lucide-react";
import { Badge } from "@/components/ui/base/badge";
import { Button } from "@/components/ui/base/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/base/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/base/popover";
import { Separator } from "@/components/ui/base/separator";
import { cn } from "@/lib/utils";

interface FacetedFilterOption {
  label: string;
  value: string;
}

interface DataTableFacetedFilterProps<TData, TValue> {
  /** Table-backed mode: filters the rows the table currently holds. */
  column?: Column<TData, TValue>;
  title: string;
  options: FacetedFilterOption[];
  /**
   * Controlled mode. Supply these to filter something wider than the table's
   * own rows — a list paginated outside the table, for instance, where a
   * column filter would only ever see the current page.
   */
  selected?: string[];
  onSelectedChange?: (values: string[]) => void;
  /** Option counts for controlled mode; the column supplies its own. */
  facetCounts?: Map<string, number>;
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  options,
  selected,
  onSelectedChange,
  facetCounts,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const facets = facetCounts ?? column?.getFacetedUniqueValues();
  const selectedValues = new Set(
    selected ?? (column?.getFilterValue() as string[] | undefined) ?? [],
  );

  const commit = (values: string[]) => {
    if (onSelectedChange) {
      onSelectedChange(values);
      return;
    }
    column?.setFilterValue(values.length > 0 ? values : undefined);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 w-full justify-start border-dashed px-2 text-xs"
        >
          <PlusCircle className="mr-1.5 h-3.5 w-3.5" />
          {title}
          {selectedValues.size > 0 && (
            <>
              <Separator orientation="vertical" className="mx-1.5 h-3" />
              <Badge
                variant="secondary"
                className="rounded-sm px-1 text-[10px] font-normal"
              >
                {selectedValues.size}
              </Badge>
            </>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0" align="start">
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>No results found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const isSelected = selectedValues.has(option.value);
                const count = facets?.get(option.value) ?? 0;
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => {
                      const next = new Set(selectedValues);
                      if (isSelected) {
                        next.delete(option.value);
                      } else {
                        next.add(option.value);
                      }
                      const filterValue =
                        next.size > 0 ? Array.from(next) : undefined;
                      commit(filterValue ?? []);
                    }}
                  >
                    <div
                      className={cn(
                        "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        isSelected
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <Check className="h-3 w-3" />
                    </div>
                    <span>{option.label}</span>
                    <span className="ml-auto flex h-4 w-4 items-center justify-center font-mono text-xs">
                      {count}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => commit([])}
                    className="justify-center text-center"
                  >
                    Clear filters
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
